/**
 * BEZ Capital API 服务
 * 后端接口统一管理
 */

import axios from 'axios'

// API 基础配置
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8081/api'

// 创建 axios 实例
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  }
})

// 请求拦截器 - 添加 Token
apiClient.interceptors.request.use(
  config => {
    // 从 localStorage 获取 token
    const token = localStorage.getItem('authToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    console.log('🚀 API Request:', config.method.toUpperCase(), config.url)
    return config
  },
  error => {
    console.error('❌ Request Error:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器 - 统一处理响应
apiClient.interceptors.response.use(
  response => {
    console.log('✅ API Response:', response.config.url, response.data)
    // 调试：查看客户列表的第一个客户数据
    if (response.config.url.includes('/clients/page') && response.data.data && response.data.data.content) {
      console.log('🔍 First Client Data:', JSON.stringify(response.data.data.content[0], null, 2))
    }
    return response.data
  },
  error => {
    console.error('❌ Response Error:', error.response?.data || error.message)

    // 401 未授权 - 跳转到登录页
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken')
      localStorage.removeItem('userInfo')
      window.location.href = '/login'
    }

    return Promise.reject(error)
  }
)

// ==================== 认证 API ====================

/**
 * 用户登录
 * @param {string} email - 邮箱
 * @param {string} password - 密码
 * @returns {Promise} 登录响应
 */
export const login = (email, password) => {
  return apiClient.post('/auth/login', { email, password })
}

/**
 * 用户注册
 * @param {Object} userData - 用户数据
 * @returns {Promise} 注册响应
 */
export const register = (userData) => {
  return apiClient.post('/auth/register', userData)
}

/**
 * 获取当前用户信息
 * @returns {Promise} 用户信息
 */
export const getCurrentUser = () => {
  return apiClient.get('/auth/me')
}

/**
 * 退出登录
 */
export const logout = () => {
  localStorage.removeItem('authToken')
  localStorage.removeItem('userInfo')
  window.location.href = '/login'
}

// ==================== 客户管理 API ====================

/**
 * 获取所有客户（不分页）
 * @returns {Promise} 客户列表
 */
export const getAllClients = () => {
  return apiClient.get('/clients')
}

/**
 * 分页查询客户（支持搜索）
 * @param {number} page - 页码（从0开始）
 * @param {number} size - 每页大小
 * @param {string} keyword - 搜索关键词（可选）
 * @param {string} sortBy - 排序字段
 * @param {string} sortDir - 排序方向（asc/desc）
 * @returns {Promise} 分页客户列表
 */
export const getClientsPage = (page = 0, size = 10, keyword = '', sortBy = 'id', sortDir = 'desc') => {
  const params = { page, size, sortBy, sortDir }
  if (keyword) {
    params.keyword = keyword
  }
  return apiClient.get('/clients/page', { params })
}

/**
 * 根据 ID 获取客户
 * @param {number} id - 客户 ID
 * @returns {Promise} 客户信息
 */
export const getClientById = (id) => {
  return apiClient.get(`/clients/${id}`)
}

/**
 * 根据 client_id 获取客户
 * @param {string} clientId - 客户编号
 * @returns {Promise} 客户信息
 */
export const getClientByClientId = (clientId) => {
  return apiClient.get(`/clients/by-client-id/${clientId}`)
}

/**
 * 根据邮箱获取客户
 * @param {string} email - 邮箱
 * @returns {Promise} 客户信息
 */
export const getClientByEmail = (email) => {
  return apiClient.get(`/clients/by-email/${email}`)
}

/**
 * 创建客户
 * @param {Object} clientData - 客户数据
 * @returns {Promise} 创建结果
 */
export const createClient = (clientData) => {
  return apiClient.post('/clients', clientData)
}

/**
 * 更新客户
 * @param {number} id - 客户 ID
 * @param {Object} clientData - 客户数据
 * @returns {Promise} 更新结果
 */
export const updateClient = (id, clientData) => {
  return apiClient.put(`/clients/${id}`, clientData)
}

/**
 * 删除客户
 * @param {number} id - 客户 ID
 * @returns {Promise} 删除结果
 */
export const deleteClient = (id) => {
  return apiClient.delete(`/clients/${id}`)
}

/**
 * 调整客户余额
 * @param {number} id - 客户 ID
 * @param {Object} adjustmentData - 调整数据 { amount, type, remark }
 * @returns {Promise} 调整结果
 */
export const adjustClientBalance = (id, adjustmentData) => {
  return apiClient.post(`/clients/${id}/adjust-balance`, adjustmentData)
}

/**
 * 客户加入池子或追加投入
 * @param {number} id - 客户 ID
 * @param {Object} joinData - 加入数据 { poolId, amount }
 * @returns {Promise} 加入结果
 */
export const joinPool = (id, joinData) => {
  return apiClient.post(`/clients/${id}/join-pool`, joinData)
}

/**
 * 客户赎回（减少投入）
 * @param {number} id - 客户 ID
 * @param {Object} redeemData - 赎回数据 { poolId, amount }
 * @returns {Promise} 赎回结果
 */
export const redeemFromPool = (id, redeemData) => {
  return apiClient.post(`/clients/${id}/redeem`, redeemData)
}

/**
 * 获取客户的所有投资记录
 * @param {number} id - 客户 ID
 * @returns {Promise} 投资记录列表
 */
export const getClientInvestments = (id) => {
  return apiClient.get(`/clients/${id}/investments`)
}

/**
 * 根据客户编号获取投资记录
 * @param {string} clientId - 客户编号
 * @returns {Promise} 投资记录列表
 */
export const getInvestmentsByClientId = (clientId) => {
  return apiClient.get(`/clients/by-client-id/${clientId}/investments`)
}

// ==================== 策略池管理 API ====================

/**
 * 获取所有策略池
 * @returns {Promise} 策略池列表
 */
export const getAllPools = () => {
  return apiClient.get('/pools')
}

/**
 * 根据 ID 获取策略池
 * @param {number} id - 策略池 ID
 * @returns {Promise} 策略池信息
 */
export const getPoolById = (id) => {
  return apiClient.get(`/pools/${id}`)
}

/**
 * 根据 pool_id 获取策略池
 * @param {string} poolId - 策略池编号
 * @returns {Promise} 策略池信息
 */
export const getPoolByPoolId = (poolId) => {
  return apiClient.get(`/pools/by-pool-id/${poolId}`)
}

/**
 * 创建策略池
 * @param {Object} poolData - 策略池数据
 * @returns {Promise} 创建结果
 */
export const createPool = (poolData) => {
  return apiClient.post('/pools', poolData)
}

/**
 * 更新策略池
 * @param {number} id - 策略池 ID
 * @param {Object} poolData - 策略池数据
 * @returns {Promise} 更新结果
 */
export const updatePool = (id, poolData) => {
  return apiClient.put(`/pools/${id}`, poolData)
}

/**
 * 删除策略池
 * @param {number} id - 策略池 ID
 * @returns {Promise} 删除结果
 */
export const deletePool = (id) => {
  return apiClient.delete(`/pools/${id}`)
}

/**
 * 获取池子的所有客户
 * @param {string} poolId - 池子ID
 * @returns {Promise} 客户列表
 */
export const getPoolClients = (poolId) => {
  return apiClient.get(`/pools/${poolId}/clients`)
}

// ==================== 净值记录管理 API ====================

/**
 * 获取所有净值记录（分页）
 * @param {Object} params - 查询参数 { page, size, sortBy, sortDir }
 * @returns {Promise} 净值记录列表
 */
export const getAllNavRecords = (params = {}) => {
  return apiClient.get('/nav-records', { params })
}

/**
 * 根据客户 ID 获取净值曲线（客户净值快照）
 * @param {string} clientId - 客户编号
 * @param {number} days - 天数（null或0表示从加入日期开始，正数表示最近N天）
 * @returns {Promise} 客户净值快照列表
 */
export const getClientNavCurve = (clientId, days = null) => {
  const params = days ? { days } : {}
  return apiClient.get(`/nav-records/client/${clientId}/curve`, { params })
}

/**
 * 获取所有客户净值快照（分页）
 * @param {Object} params - 查询参数 { page, size, sortBy, sortDir }
 * @returns {Promise} 客户净值快照列表
 */
export const getAllClientNavSnapshots = (params = {}) => {
  return apiClient.get('/nav-records/client-snapshots', { params })
}

/**
 * 根据客户 ID 获取净值记录（旧版，保留兼容性）
 * @param {string} clientId - 客户编号
 * @returns {Promise} 净值记录列表
 * @deprecated 使用 getClientNavCurve 代替
 */
export const getNavRecordsByClientId = (clientId) => {
  return getClientNavCurve(clientId)
}

/**
 * 根据客户 ID 和日期范围获取净值记录
 * @param {string} clientId - 客户编号
 * @param {string} startDate - 开始日期 (YYYY-MM-DD)
 * @param {string} endDate - 结束日期 (YYYY-MM-DD)
 * @returns {Promise} 净值记录列表
 */
export const getNavRecordsByClientIdAndDateRange = (clientId, startDate, endDate) => {
  return apiClient.get(`/nav-records/client/${clientId}/date-range`, {
    params: { startDate, endDate }
  })
}

/**
 * 根据策略池 ID 获取净值记录
 * @param {string} poolId - 策略池编号
 * @returns {Promise} 净值记录列表
 */
export const getNavRecordsByPoolId = (poolId) => {
  return apiClient.get(`/nav-records/pool/${poolId}`)
}

/**
 * 获取池子成员汇总信息（包括总金额）
 * @param {string} poolId - 策略池编号
 * @returns {Promise} 成员汇总信息
 */
export const getPoolMembersSummary = (poolId) => {
  return apiClient.get(`/nav-records/pool/${poolId}/members-summary`)
}

/**
 * 根据策略池 ID 和日期范围获取净值记录
 * @param {string} poolId - 策略池编号
 * @param {string} startDate - 开始日期 (YYYY-MM-DD)
 * @param {string} endDate - 结束日期 (YYYY-MM-DD)
 * @returns {Promise} 净值记录列表
 */
export const getNavRecordsByPoolIdAndDateRange = (poolId, startDate, endDate) => {
  return apiClient.get(`/nav-records/pool/${poolId}/date-range`, {
    params: { startDate, endDate }
  })
}

/**
 * 创建单条净值记录
 * @param {Object} navRecordData - 净值记录数据
 * @returns {Promise} 创建结果
 */
export const createNavRecord = (navRecordData) => {
  return apiClient.post('/nav-records', navRecordData)
}

/**
 * 批量创建净值记录
 * @param {Array} navRecordsData - 净值记录数组
 * @returns {Promise} 创建结果
 */
export const createNavRecordsBatch = (navRecordsData) => {
  return apiClient.post('/nav-records/batch', { records: navRecordsData })
}

/**
 * 创建池子净值记录（新的份额制系统）
 * @param {Object} poolNavData - 池子净值数据 { poolId, date, totalValue }
 * @returns {Promise} 创建结果
 */
export const createPoolNavRecord = (poolNavData) => {
  return apiClient.post('/nav-records/pool', poolNavData)
}

/**
 * 删除净值记录
 * @param {number} id - 净值记录 ID
 * @returns {Promise} 删除结果
 */
export const deleteNavRecord = (id) => {
  return apiClient.delete(`/nav-records/${id}`)
}

/**
 * 撤回指定池子和日期的净值更新
 * @param {string} poolId - 池子ID
 * @param {string} date - 日期 (YYYY-MM-DD)
 * @returns {Promise} 撤回结果
 */
export const rollbackNavUpdate = (poolId, date) => {
  return apiClient.delete('/nav-records/rollback', {
    params: { poolId, date }
  })
}

// ==================== 工具函数 ====================

/**
 * 保存登录信息
 * @param {string} token - JWT Token
 * @param {Object} userInfo - 用户信息
 */
export const saveAuthInfo = (token, userInfo) => {
  localStorage.setItem('authToken', token)
  localStorage.setItem('userInfo', JSON.stringify(userInfo))
}

/**
 * 获取登录信息
 * @returns {Object} { token, userInfo }
 */
export const getAuthInfo = () => {
  const token = localStorage.getItem('authToken')
  const userInfo = localStorage.getItem('userInfo')
  return {
    token,
    userInfo: userInfo ? JSON.parse(userInfo) : null
  }
}

/**
 * 检查是否已登录
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  return !!localStorage.getItem('authToken')
}

// ==================== 份额管理 API ====================

/**
 * 获取所有份额变动记录（分页）
 * @param {number} page - 页码
 * @param {number} size - 每页大小
 * @returns {Promise} 份额变动记录列表
 */
export const getShareTransactions = (page = 0, size = 50) => {
  return apiClient.get('/share-transactions/page', { params: { page, size } })
}

/**
 * 根据客户ID获取份额变动记录（分页）
 * @param {number} clientId - 客户ID
 * @param {number} page - 页码
 * @param {number} size - 每页大小
 * @returns {Promise} 份额变动记录列表
 */
export const getShareTransactionsByClientId = (clientId, page = 0, size = 50) => {
  return apiClient.get(`/share-transactions/client/${clientId}/page`, { params: { page, size } })
}

/**
 * 根据客户编号获取份额变动记录
 * @param {string} clientCode - 客户编号（如 C001）
 * @returns {Promise} 份额变动记录列表
 */
export const getShareTransactionsByClientCode = (clientCode) => {
  return apiClient.get(`/share-transactions/client-code/${clientCode}`)
}

/**
 * 根据池子ID获取份额变动记录
 * @param {number} poolId - 池子ID
 * @returns {Promise} 份额变动记录列表
 */
export const getShareTransactionsByPoolId = (poolId) => {
  return apiClient.get(`/share-transactions/pool/${poolId}`)
}

/**
 * 根据日期范围获取份额变动记录
 * @param {string} startDate - 开始日期（YYYY-MM-DD）
 * @param {string} endDate - 结束日期（YYYY-MM-DD）
 * @returns {Promise} 份额变动记录列表
 */
export const getShareTransactionsByDateRange = (startDate, endDate) => {
  return apiClient.get('/share-transactions/date-range', { params: { startDate, endDate } })
}


// 默认导出
export default {
  // 认证
  login,
  register,
  getCurrentUser,
  logout,

  // 客户管理
  getAllClients,
  getClientById,
  getClientByClientId,
  getClientByEmail,
  createClient,
  updateClient,
  deleteClient,
  adjustClientBalance,
  joinPool,
  redeemFromPool,
  getClientInvestments,
  getInvestmentsByClientId,

  // 策略池管理
  getAllPools,
  getPoolById,
  getPoolByPoolId,
  createPool,
  updatePool,
  deletePool,
  getPoolClients,

  // 净值记录管理
  getAllNavRecords,
  getClientNavCurve,
  getAllClientNavSnapshots,
  getNavRecordsByClientId,
  getNavRecordsByClientIdAndDateRange,
  getNavRecordsByPoolId,
  getPoolMembersSummary,
  getNavRecordsByPoolIdAndDateRange,
  createNavRecord,
  createNavRecordsBatch,
  createPoolNavRecord,
  deleteNavRecord,
  rollbackNavUpdate,

  // 工具函数
  saveAuthInfo,
  getAuthInfo,
  isAuthenticated,

  // 份额管理
  getShareTransactions,
  getShareTransactionsByClientId,
  getShareTransactionsByClientCode,
  getShareTransactionsByPoolId,
  getShareTransactionsByDateRange
}

